// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import setNotes from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetNotes";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import validateOverrideAddress from "@mcmc/App/Handlers/Pages/CheckoutShipping/ValidateOverrideAddress";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { NotifyBody } from "@mcmc/XDL/Components/Notify";
import { resetRetainScrollPosition } from "@mcmc/XDL/Utilities";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    validateOverrideAddress,
    setNotes,
    loadCurrentCart,
};

const mapStateToProps = (state: McMcApplicationState) => ({
    cartId: state.pages.checkoutShipping.cartId,
    isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
    reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
    location: getLocation(state),
    isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
    overrideShipTo: state.xCustomCheckoutShipping.overrideShipTo,
    nextCheckoutStep: state.xCustomCheckoutShipping.nextStepHash,
    cart: getCurrentCartState(state),
});

type Props = PageProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    HasToasterContext;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
        };
    }

    async componentDidMount() {
        const parsedQuery = parseQueryString<{ cartId?: string }>(this.props.location.search);
        const cartId = parsedQuery.cartId;
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
        }

        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
        this.props.setInitialValues();

        const { cart, loadCurrentCart } = this.props;
        if (!cartId && !cart.isLoading) {
            loadCurrentCart();
        }
    }

    componentWillUnmount() {
        this.props.setNotes({ notes: "" });
    }

    scrollToFirstFormError = () => {
        initializeScrollTo("[aria-invalid='true']");
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));
        const continueCheckout = async () => {
            const parsedQuery = parseQueryString<{ express?: boolean }>(this.props.location.search);
            this.setState({ isRedirecting: true });
            // TODO ISC-12556
            await this.props.updateCart({});
            let checkoutStepHash = this.props.nextCheckoutStep ? `step=${this.props.nextCheckoutStep}` : "";
            checkoutStepHash =
                checkoutStepHash && (this.props.cartId ? `&${checkoutStepHash}` : `?${checkoutStepHash}`);
            let continueUrl = this.props.cartId
                ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}${checkoutStepHash}`
                : `${this.props.reviewAndSubmitPageLink!.url}${checkoutStepHash}`;
            if (parsedQuery.express) {
                continueUrl += `${this.props.cartId || checkoutStepHash ? "&" : "?"}express=true`;
            }

            resetRetainScrollPosition(continueUrl);
            this.props.history.replace(continueUrl);
        };
        if (results.every(o => o)) {
            if (this.props.overrideShipTo) {
                this.props.validateOverrideAddress({
                    onError: erorMessage => {
                        this.props.toaster.addToast({
                            body: (
                                <NotifyBody>
                                    <Typography className="title">
                                        {translate("Override Address Validation")}
                                    </Typography>
                                    {erorMessage}
                                </NotifyBody>
                            ),
                            messageType: "danger",
                            timeoutLength: 10000,
                        });
                    },
                    onSuccess: result => {
                        if (result.isValid && result.normalizedAddresses.length === 0) {
                            continueCheckout();
                        }
                    },
                });
            } else {
                continueCheckout();
            }
        } else {
            this.scrollToFirstFormError();
        }
    };

    formPreventDefault = (event: any) => {
        const targetTagName = event.target.tagName.toLowerCase();
        const parentTargetTagName = event.target.parentElement?.tagName.toLowerCase();
        const clickedElement = targetTagName === "button" ? event.target : event.target.parentElement;
        if (targetTagName === "button" || parentTargetTagName === "button") {
            if (clickedElement.getAttribute("type") !== "submit") {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                {/* XNG-Changes: adding this condition to CartLayout to avoid double spinner */}
                {/* <LoadingOverlay loading={this.props.isUpdatingCustomer}> */}
                <CheckoutShippingFormContext.Provider value={this.state}>
                    <form
                        id="checkoutShippingForm"
                        onSubmit={this.handleFormSubmit}
                        onClick={this.formPreventDefault}
                        noValidate
                    >
                        <Zone zoneName="Content" contentId={this.props.id} />
                    </form>
                </CheckoutShippingFormContext.Provider>
                {/* </LoadingOverlay> */}
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(CheckoutShippingPage))),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
